import PropTypes from "prop-types";
import Grid from "@mui/material/Grid";
import Skeleton from "@mui/material/Skeleton";
import Tooltip from "@mui/material/Tooltip";
import VuiBox from "components/ui/VuiBox";
import VuiTypography from "components/ui/VuiTypography";
import GlassCard from "components/GlassCard";

// Truncate a company name to its first word: "Terumo Neuro" → "Terumo"
const firstWord = (name) => (name ? name.split(" ")[0] : name);
const CLIENT_COLOR = "#0075FF";
const INDUSTRY_COLOR = "#2CD9FF";

// Format absolute numbers: 4200 → "4.2k", 0.0 → "0"
const fmt = (n, decimals = 1) => {
  if (n == null) return "—";
  if (n >= 10000) return `${(n / 1000).toFixed(decimals)}k`;
  if (n >= 1000) return `${(n / 1000).toFixed(decimals)}k`;
  if (Number.isInteger(n)) return String(n);
  return n.toFixed(decimals);
};

/**
 * Delta badge — shows % difference vs industry avg.
 * Positive = client is above average (good), negative = below.
 */
function DeltaBadge({ clientVal, industryVal }) {
  if (clientVal == null || industryVal == null || industryVal === 0) return null;
  const pct = Math.round(((clientVal - industryVal) / industryVal) * 100);
  const isAbove = pct >= 0;
  const label = isAbove ? `+${pct}%` : `${pct}%`;
  const bg = isAbove ? "rgba(34,197,94,0.15)" : "rgba(239,68,68,0.15)";
  const border = isAbove ? "rgba(34,197,94,0.35)" : "rgba(239,68,68,0.35)";
  const color = isAbove ? "#4ade80" : "#f87171";
  return (
    <VuiBox
      sx={{
        px: 0.75, py: 0.15, borderRadius: "6px",
        background: bg, border: `1px solid ${border}`,
        display: "inline-flex", alignItems: "center",
      }}
    >
      <VuiTypography variant="caption" sx={{ fontSize: "0.68rem", color, fontWeight: "700" }}>
        {label}
      </VuiTypography>
    </VuiBox>
  );
}

/**
 * BenchmarkCard
 *
 * Shows one metric with a client value row and an industry-average row,
 * plus a delta badge comparing them. Icon displayed top-right.
 */
function BenchmarkCard({ label, clientName, industryLabel, clientValue, industryValue, clientRaw, industryRaw, loading, tooltip }) {
  const card = (
    <GlassCard
      sx={{
        height: "100%",
        "&:hover": { transform: "none", boxShadow: "none" },
      }}
    >
      {/* Label row — title left, delta badge right */}
      <VuiBox display="flex" alignItems="center" justifyContent="space-between" mb={1}>
        <VuiTypography
          variant="caption"
          sx={{
            color: "rgba(255,255,255,0.9)",
            fontSize: "0.68rem",
            letterSpacing: "0.07em",
            textTransform: "uppercase",
          }}
        >
          {label}
        </VuiTypography>
        {!loading && (
          <DeltaBadge clientVal={clientRaw} industryVal={industryRaw} />
        )}
      </VuiBox>

      {loading ? (
        <>
          <Skeleton variant="text" width="70%" height={18} sx={{ bgcolor: "rgba(255,255,255,0.07)", mb: 0.5 }} />
          <Skeleton variant="text" width="55%" height={18} sx={{ bgcolor: "rgba(255,255,255,0.07)" }} />
        </>
      ) : (
        <>
          {/* Client row */}
          <VuiBox display="flex" alignItems="baseline" gap={0.75} mb={0.5}>
            <VuiBox sx={{ width: 6, height: 6, borderRadius: "50%", background: CLIENT_COLOR, flexShrink: 0, mt: "2px" }} />
            <VuiTypography variant="caption" sx={{ fontSize: "0.72rem", color: "rgba(255,255,255,0.5)", flexShrink: 0 }}>
              {firstWord(clientName)}:
            </VuiTypography>
            <VuiTypography variant="caption" sx={{ fontSize: "0.88rem", color: "rgba(255,255,255,1)", fontWeight: "regular" }}>
              {clientValue}
            </VuiTypography>
          </VuiBox>

          {/* Industry avg row */}
          <VuiBox display="flex" alignItems="baseline" gap={0.75}>
            <VuiBox sx={{ width: 6, height: 6, borderRadius: "50%", background: INDUSTRY_COLOR, flexShrink: 0, mt: "2px" }} />
            <VuiTypography variant="caption" sx={{ fontSize: "0.72rem", color: "rgba(255,255,255,0.38)", flexShrink: 0 }}>
              {industryLabel}:
            </VuiTypography>
            <VuiTypography variant="caption" sx={{ fontSize: "0.88rem", color: "rgba(255,255,255,1)", fontWeight: "regular" }}>
              {industryValue}
            </VuiTypography>
          </VuiBox>
        </>
      )}
    </GlassCard>
  );

  return tooltip ? (
    <Tooltip title={tooltip} placement="top" arrow>
      <span style={{ display: "block", height: "100%" }}>{card}</span>
    </Tooltip>
  ) : card;
}

/**
 * IndustryBenchmarkStrip
 *
 * Four KPI cards shown in the "All Competitors" all-view.
 * Compares the client's LinkedIn stats against the average of all tracked competitors.
 *
 * Props:
 *   clientStats    — DigitalPresenceEngagementDTO for the client
 *   industryAvg    — averaged DigitalPresenceEngagementDTO across competitors
 *   clientName     — display name for the client
 *   competitorCount— number of competitors in the average
 *   loading        — show skeleton state
 */
function IndustryBenchmarkStrip({ clientStats, industryAvg, clientName, competitorCount, loading }) {
  const n = competitorCount || 0;

  const industryLabel = "Industry avg";

  const cards = [
    {
      label: "Posts Published",
      clientValue: clientStats?.totalPosts ?? "—",
      industryValue: industryAvg?.totalPosts != null ? `${industryAvg.totalPosts}` : "—",
      clientRaw: clientStats?.totalPosts,
      industryRaw: industryAvg?.totalPosts,
    },
    {
      label: "Total Engagements",
      clientValue: fmt(clientStats?.totalEngagements),
      industryValue: industryAvg?.totalEngagements != null ? `${fmt(industryAvg.totalEngagements)}` : "—",
      clientRaw: clientStats?.totalEngagements,
      industryRaw: industryAvg?.totalEngagements,
    },
    {
      label: "Avg Eng / Post",
      clientValue: clientStats ? Math.round(clientStats.avgEngagementsPerPost ?? 0) : "—",
      industryValue: industryAvg?.avgEngagementsPerPost != null
        ? `${Math.round(industryAvg.avgEngagementsPerPost)}`
        : "—",
      clientRaw: clientStats?.avgEngagementsPerPost,
      industryRaw: industryAvg?.avgEngagementsPerPost,
      tooltip: "Average engagements (likes + comments + shares) per post",
    },
    {
      label: "Top Content Category",
      clientValue: clientStats?.mostCommonContentCategory ?? "—",
      industryValue: industryAvg?.mostCommonContentCategory ?? "—",
      clientRaw: null,
      industryRaw: null,
    },
  ];

  return (
    <VuiBox>
      {/* Section header */}
      <VuiBox display="flex" alignItems="center" gap={1} mb={1.5}>
        <VuiTypography variant="caption" sx={{ color: "rgba(255,255,255,0.35)", fontSize: "0.72rem", letterSpacing: "0.07em", textTransform: "uppercase" }}>
          Industry Benchmark
        </VuiTypography>
        {!loading && n > 0 && (
          <VuiTypography variant="caption" sx={{ color: "rgba(255,255,255,0.22)", fontSize: "0.68rem" }}>
            · avg across {n} competitor{n !== 1 ? "s" : ""}
          </VuiTypography>
        )}
      </VuiBox>

      <Grid container spacing={2}>
        {cards.map((card) => (
          <Grid key={card.label} item xs={12} sm={6} lg={3}>
            <BenchmarkCard
              label={card.label}
              clientName={clientName}
              industryLabel={industryLabel}
              clientValue={card.clientValue}
              industryValue={card.industryValue}
              clientRaw={card.clientRaw}
              industryRaw={card.industryRaw}
              loading={loading}
              tooltip={card.tooltip}
            />
          </Grid>
        ))}
      </Grid>
    </VuiBox>
  );
}

IndustryBenchmarkStrip.propTypes = {
  clientStats: PropTypes.object,
  industryAvg: PropTypes.object,
  clientName: PropTypes.string,
  competitorCount: PropTypes.number,
  loading: PropTypes.bool,
};

IndustryBenchmarkStrip.defaultProps = {
  clientStats: null,
  industryAvg: null,
  clientName: "Your Company",
  competitorCount: 0,
  loading: false,
};

export default IndustryBenchmarkStrip;
